import React, {useEffect} from 'react';
import {connect} from 'react-redux';
import {divide, toNumber} from 'lodash';

import {Dispatch, Store} from 'spectra-logic-ui';
import {fetchResource} from 'spectra-logic-ui/actions';
import {Table, Tooltip} from 'spectra-logic-ui/components';
import {commafy, numberToHumanSize} from 'spectra-logic-ui/helpers/number';

import {Metrics} from '@/types';
import Card from 'spectra-logic-ui/components/card';

type Props = {
  id: string;
  usedBy?: string[];
  metrics?: Metrics;
  error?: boolean;
  fetching?: boolean;
  fetchMetrics?: () => Promise<any>;
}

const sizeTip = 'This is the total of the original object sizes before compression and encoding.';
const storedTip = 'This is the size of all object content after compression and encoding.';
const optionalTip = 'Optional data is stored to improve performance when there is sufficient '+
  'space available. Optional data is deleted automatically when space is needed.';

const MetricsDetails = ({id, usedBy, fetchMetrics, metrics={}, error=false, fetching=false}: Props) => {
  useEffect(() => {
    if (fetchMetrics !== undefined) {
      fetchMetrics();
    }
  }, [id]);

  if (!id) {
    return (<Card><Card.Body>No usage data available</Card.Body></Card>);
  }
  return (
    <Table>
      <Table.Body isLoading={fetching} hasError={error}>
        <Table.Row>
          <Table.Cell>Number of objects stored</Table.Cell>
          <Table.Cell>{commafy(metrics.count)}</Table.Cell>
        </Table.Row>
        <Table.Row>
          <Table.Cell>Total size of objects</Table.Cell>
          <Table.Cell>{numberToHumanSize(metrics.size)}<Tooltip>{sizeTip}</Tooltip></Table.Cell>
        </Table.Row>
        <Table.Row>
          <Table.Cell>Total stored size</Table.Cell>
          <Table.Cell>{numberToHumanSize(metrics.stored)}<Tooltip>{storedTip}</Tooltip></Table.Cell>
        </Table.Row>
        {metrics.optional !== undefined &&
          <Table.Row>
            <Table.Cell>Total size of optional data</Table.Cell>
            <Table.Cell>{numberToHumanSize(metrics.optional)}<Tooltip>{optionalTip}</Tooltip></Table.Cell>
          </Table.Row>
        }
        <Table.Row>
          <Table.Cell>Average stored size</Table.Cell>
          <Table.Cell>{numberToHumanSize(divide(toNumber(metrics.stored), toNumber(metrics.count)))}</Table.Cell>
        </Table.Row>
        {usedBy !== undefined &&
          <Table.Row>
            <Table.Cell>Used by buckets</Table.Cell>
            <Table.Cell>{usedBy.join(', ')}</Table.Cell>
          </Table.Row>
        }
      </Table.Body>
    </Table>
  );
};

const mapStateToProps = (state: Store, {id}: Props) => {
  const metricsResource = state.resources[`storage/${id}/metrics`] || {};
  return {
    metrics: metricsResource.data,
    error: metricsResource.error,
    fetching: metricsResource.fetching,
  };
};

const mapDispatchToProps = (dispatch: Dispatch, {id}: Props) => ({
  fetchMetrics: () => {
    // If the id is not set, return empty metrics
    if (!id) {
      return Promise.resolve({});
    } else {
      return dispatch(fetchResource(`storage/${id}/metrics`));
    }
  },
});

export default connect(mapStateToProps, mapDispatchToProps)(MetricsDetails);
